import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { vehiclesAPI, trailersAPI, serviceAPI, dashboardAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  Wrench, 
  Truck,
  Flame,
  Calendar,
  Gauge,
  Plus,
  Search,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ChevronRight,
  Upload,
  FileText,
  Pencil,
  Trash2,
  Save,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const ServiceSchedulePage = () => {
  const navigate = useNavigate();
  const { isSupervisor, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [extinguishers, setExtinguishers] = useState([]);
  const [serviceAlerts, setServiceAlerts] = useState(null);
  const [serviceRecords, setServiceRecords] = useState([]);
  const [kmReadings, setKmReadings] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialog states
  const [showKMDialog, setShowKMDialog] = useState(false);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [showExtinguisherDialog, setShowExtinguisherDialog] = useState(false);
  const [showServiceDetailDialog, setShowServiceDetailDialog] = useState(false);
  const [selectedServiceRecord, setSelectedServiceRecord] = useState(null);
  const [editingRecord, setEditingRecord] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Form states
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [kmReading, setKmReading] = useState('');
  const [kmNotes, setKmNotes] = useState('');
  const [kmOverride, setKmOverride] = useState(false);
  
  const [serviceForm, setServiceForm] = useState({
    service_type: 'vehicle_service',
    entity_type: 'vehicle',
    entity_id: '',
    entity_registration: '',
    service_date: new Date().toISOString().split('T')[0],
    service_km: '',
    service_provider: '',
    invoice_number: '',
    notes: ''
  });
  
  const [invoiceFile, setInvoiceFile] = useState(null);
  
  const [extinguisherForm, setExtinguisherForm] = useState({
    serial_number: '',
    entity_type: 'vehicle',
    entity_id: '',
    entity_registration: '',
    extinguisher_type: 'DCP',
    size_kg: 9,
    last_service_date: '',
    service_interval_months: 12
  });
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [vehiclesRes, trailersRes, alertsRes, recordsRes, extRes] = await Promise.all([
        vehiclesAPI.list(),
        trailersAPI.list(),
        dashboardAPI.serviceAlerts(),
        serviceAPI.listServiceRecords({ limit: 50 }),
        serviceAPI.listExtinguishers()
      ]);
      
      setVehicles(vehiclesRes.data);
      setTrailers(trailersRes.data);
      setServiceAlerts(alertsRes.data);
      setServiceRecords(recordsRes.data);
      setExtinguishers(extRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load service data');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordKM = async () => {
    if (!selectedVehicle || !kmReading) {
      toast.error('Please select a vehicle and enter KM reading');
      return;
    }
    
    try {
      setSubmitting(true);
      await serviceAPI.createKMReading(selectedVehicle, parseInt(kmReading), null, kmNotes, kmOverride);
      toast.success(kmOverride ? 'KM correction recorded' : 'KM reading recorded');
      setShowKMDialog(false);
      setSelectedVehicle('');
      setKmReading('');
      setKmNotes('');
      setKmOverride(false);
      fetchData();
    } catch (error) {
      console.error('Failed to record KM:', error);
      toast.error(error.response?.data?.detail || 'Failed to record KM reading');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordService = async () => {
    if (!serviceForm.entity_id || !serviceForm.service_date) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      const payload = {
        ...serviceForm,
        service_date: new Date(serviceForm.service_date).toISOString(),
        service_km: serviceForm.service_km ? parseInt(serviceForm.service_km) : null
      };
      const response = await serviceAPI.createServiceRecord(payload);
      const recordId = response.data.id;
      
      // Upload invoice if provided
      if (invoiceFile && recordId) {
        try {
          await serviceAPI.uploadInvoice(recordId, invoiceFile);
          toast.success('Service recorded with invoice uploaded');
        } catch (uploadError) {
          console.error('Failed to upload invoice:', uploadError);
          toast.success('Service recorded (invoice upload failed - you can upload it later)');
        }
      } else {
        toast.success('Service recorded successfully');
      }
      
      setShowServiceDialog(false);
      setServiceForm({
        service_type: 'vehicle_service',
        entity_type: 'vehicle',
        entity_id: '',
        entity_registration: '',
        service_date: new Date().toISOString().split('T')[0],
        service_km: '',
        service_provider: '',
        invoice_number: '',
        notes: ''
      });
      setInvoiceFile(null);
      fetchData();
    } catch (error) {
      console.error('Failed to record service:', error);
      toast.error(error.response?.data?.detail || 'Failed to record service');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddExtinguisher = async () => {
    if (!extinguisherForm.serial_number || !extinguisherForm.entity_id) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      const payload = {
        ...extinguisherForm,
        last_service_date: extinguisherForm.last_service_date 
          ? new Date(extinguisherForm.last_service_date).toISOString() 
          : null
      };
      await serviceAPI.createExtinguisher(payload);
      toast.success('Fire extinguisher added');
      setShowExtinguisherDialog(false);
      setExtinguisherForm({
        serial_number: '',
        entity_type: 'vehicle',
        entity_id: '',
        entity_registration: '',
        extinguisher_type: 'DCP',
        size_kg: 9,
        last_service_date: '',
        service_interval_months: 12
      });
      fetchData();
    } catch (error) {
      console.error('Failed to add extinguisher:', error);
      toast.error(error.response?.data?.detail || 'Failed to add fire extinguisher');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'overdue':
        return <Badge className="bg-red-600">OVERDUE</Badge>;
      case 'critical':
        return <Badge className="bg-red-500">Critical</Badge>;
      case 'warning':
        return <Badge className="bg-orange-500">Warning</Badge>;
      case 'attention':
        return <Badge className="bg-amber-500">Attention</Badge>;
      default:
        return <Badge className="bg-emerald-500">OK</Badge>;
    }
  };

  const handleExportReport = async () => {
    try {
      const token = localStorage.getItem('rtms_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/service-schedule`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Service_Schedule_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Service Schedule report exported');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export report');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const allEntities = [...vehicles, ...trailers.map(t => ({ ...t, type: 'trailer' }))];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="service-schedule-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            SERVICE SCHEDULE
          </h1>
          <p className="text-slate-500 mt-1">
            Track vehicle services, fire extinguishers, and KM readings
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            onClick={() => setShowKMDialog(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="record-km-btn"
          >
            <Gauge className="w-4 h-4 mr-2" />
            Record KM
          </Button>
          <Button 
            onClick={() => setShowServiceDialog(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="record-service-btn"
          >
            <Wrench className="w-4 h-4 mr-2" />
            Record Service
          </Button>
          <Button 
            variant="outline"
            onClick={handleExportReport}
            data-testid="export-report-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {serviceAlerts && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-red-500" />
                <div>
                  <p className="text-2xl font-bold text-red-600">
                    {serviceAlerts.vehicles.critical_count + 
                     serviceAlerts.trailers.critical_count + 
                     serviceAlerts.fire_extinguishers.critical_count}
                  </p>
                  <p className="text-sm text-slate-500">Critical</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-orange-500" />
                <div>
                  <p className="text-2xl font-bold text-orange-600">
                    {serviceAlerts.vehicles.warning_count + 
                     serviceAlerts.trailers.warning_count + 
                     serviceAlerts.fire_extinguishers.warning_count}
                  </p>
                  <p className="text-sm text-slate-500">Warning</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-8 h-8 text-amber-500" />
                <div>
                  <p className="text-2xl font-bold text-amber-600">
                    {serviceAlerts.vehicles.attention_count + 
                     serviceAlerts.trailers.attention_count + 
                     serviceAlerts.fire_extinguishers.attention_count}
                  </p>
                  <p className="text-sm text-slate-500">Attention</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                <div>
                  <p className="text-2xl font-bold text-emerald-600">
                    {serviceAlerts.vehicles.total + 
                     serviceAlerts.trailers.total + 
                     serviceAlerts.fire_extinguishers.total}
                  </p>
                  <p className="text-sm text-slate-500">Total Items</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="tab-vehicles">Vehicles</TabsTrigger>
          <TabsTrigger value="trailers" data-testid="tab-trailers">Trailers</TabsTrigger>
          <TabsTrigger value="extinguishers" data-testid="tab-extinguishers">Fire Extinguishers</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {/* Alerts by Category */}
          {serviceAlerts && (
            <>
              {/* Critical Alerts */}
              {(serviceAlerts.vehicles.critical_count > 0 || 
                serviceAlerts.trailers.critical_count > 0 || 
                serviceAlerts.fire_extinguishers.critical_count > 0) && (
                <Card className="border-red-300 bg-red-50">
                  <CardHeader>
                    <CardTitle className="text-red-700 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Critical - Immediate Action Required
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {serviceAlerts.vehicles.alerts.critical.map(v => (
                      <div key={v.id} className="flex justify-between items-center p-2 bg-white rounded border">
                        <div>
                          <span className="font-mono font-bold">{v.registration}</span>
                          <span className="text-sm text-slate-500 ml-2">Vehicle</span>
                        </div>
                        <div className="text-right">
                          {v.km_remaining !== null && (
                            <span className="text-red-600 text-sm mr-4">
                              {v.km_remaining <= 0 ? `Overdue by ${Math.abs(v.km_remaining)} km` : `${v.km_remaining} km remaining`}
                            </span>
                          )}
                          {v.days_remaining !== null && (
                            <span className="text-red-600 text-sm">
                              {v.days_remaining <= 0 ? `Overdue by ${Math.abs(v.days_remaining)} days` : `${v.days_remaining} days remaining`}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {serviceAlerts.trailers.alerts.critical.map(t => (
                      <div key={t.id} className="flex justify-between items-center p-2 bg-white rounded border">
                        <div>
                          <span className="font-mono font-bold">{t.registration}</span>
                          <span className="text-sm text-slate-500 ml-2">Trailer</span>
                        </div>
                        <span className="text-red-600 text-sm">
                          {t.days_remaining <= 0 ? `Overdue by ${Math.abs(t.days_remaining)} days` : `${t.days_remaining} days remaining`}
                        </span>
                      </div>
                    ))}
                    {serviceAlerts.fire_extinguishers.alerts.critical.map(e => (
                      <div key={e.id} className="flex justify-between items-center p-2 bg-white rounded border">
                        <div>
                          <span className="font-mono font-bold">{e.serial_number}</span>
                          <span className="text-sm text-slate-500 ml-2">on {e.entity_registration}</span>
                        </div>
                        <span className="text-red-600 text-sm">
                          {e.days_remaining <= 0 ? `Overdue by ${Math.abs(e.days_remaining)} days` : `${e.days_remaining} days remaining`}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Warning Alerts */}
              {(serviceAlerts.vehicles.warning_count > 0 || 
                serviceAlerts.trailers.warning_count > 0 || 
                serviceAlerts.fire_extinguishers.warning_count > 0) && (
                <Card className="border-orange-300 bg-orange-50">
                  <CardHeader>
                    <CardTitle className="text-orange-700 flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Warning - Due Within 14 Days / 1000km
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {serviceAlerts.vehicles.alerts.warning.map(v => (
                      <div key={v.id} className="flex justify-between items-center p-2 bg-white rounded border">
                        <div>
                          <span className="font-mono font-bold">{v.registration}</span>
                          <span className="text-sm text-slate-500 ml-2">Vehicle</span>
                        </div>
                        <div className="text-right">
                          {v.km_remaining !== null && <span className="text-orange-600 text-sm mr-4">{v.km_remaining} km</span>}
                          {v.days_remaining !== null && <span className="text-orange-600 text-sm">{v.days_remaining} days</span>}
                        </div>
                      </div>
                    ))}
                    {serviceAlerts.trailers.alerts.warning.map(t => (
                      <div key={t.id} className="flex justify-between items-center p-2 bg-white rounded border">
                        <div>
                          <span className="font-mono font-bold">{t.registration}</span>
                          <span className="text-sm text-slate-500 ml-2">Trailer</span>
                        </div>
                        <span className="text-orange-600 text-sm">{t.days_remaining} days</span>
                      </div>
                    ))}
                    {serviceAlerts.fire_extinguishers.alerts.warning.map(e => (
                      <div key={e.id} className="flex justify-between items-center p-2 bg-white rounded border">
                        <div>
                          <span className="font-mono font-bold">{e.serial_number}</span>
                          <span className="text-sm text-slate-500 ml-2">on {e.entity_registration}</span>
                        </div>
                        <span className="text-orange-600 text-sm">{e.days_remaining} days</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Recent Service Records */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }} className="uppercase">
                Recent Service Records
              </CardTitle>
            </CardHeader>
            <CardContent>
              {serviceRecords.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No service records yet</p>
              ) : (
                <div className="space-y-2">
                  {serviceRecords.slice(0, 10).map(record => (
                    <div 
                      key={record.id} 
                      className="flex justify-between items-center p-3 bg-slate-50 rounded hover:bg-slate-100 cursor-pointer"
                      onClick={() => {
                        setSelectedServiceRecord(record);
                        setShowServiceDetailDialog(true);
                      }}
                      data-testid={`service-record-${record.id}`}
                    >
                      <div>
                        <p className="font-mono font-bold">{record.entity_registration}</p>
                        <p className="text-sm text-slate-500">
                          {record.service_type.replace(/_/g, ' ')} • {new Date(record.service_date).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right flex items-center gap-3">
                        {record.service_km && (
                          <p className="text-sm font-mono">{record.service_km.toLocaleString()} km</p>
                        )}
                        {record.invoice_url && (
                          <Badge variant="outline" className="text-blue-600 border-blue-300">
                            <FileText className="w-3 h-3 mr-1" />
                            Invoice
                          </Badge>
                        )}
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vehicles Tab */}
        <TabsContent value="vehicles">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }} className="uppercase">
                Vehicle Service Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {vehicles.filter(v => v.vehicle_type?.toLowerCase() !== 'trailer').map(v => {
                  const alert = serviceAlerts?.vehicles.alerts.critical.find(a => a.id === v.id) ||
                               serviceAlerts?.vehicles.alerts.warning.find(a => a.id === v.id) ||
                               serviceAlerts?.vehicles.alerts.attention.find(a => a.id === v.id) ||
                               serviceAlerts?.vehicles.alerts.ok.find(a => a.id === v.id);
                  
                  return (
                    <div key={v.id} className="flex justify-between items-center p-3 bg-slate-50 rounded hover:bg-slate-100">
                      <div>
                        <p className="font-mono font-bold">{v.registration_number}</p>
                        <p className="text-sm text-slate-500">
                          {v.make} {v.model} • Current: {v.current_km?.toLocaleString() || 0} km
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm">
                          {v.next_service_km > 0 && (
                            <p>Next: {v.next_service_km?.toLocaleString()} km</p>
                          )}
                          {v.next_service_due && (
                            <p>Due: {new Date(v.next_service_due).toLocaleDateString()}</p>
                          )}
                        </div>
                        {alert && getStatusBadge(alert.status)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trailers Tab */}
        <TabsContent value="trailers">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }} className="uppercase">
                Trailer Service Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Combine trailers from trailers collection AND vehicles with type='Trailer' */}
              {(() => {
                const vehicleTrailers = vehicles.filter(v => v.vehicle_type?.toLowerCase() === 'trailer').map(v => ({
                  ...v,
                  trailer_type: v.vehicle_type,
                  source: 'vehicles'
                }));
                const allTrailers = [...trailers.map(t => ({...t, source: 'trailers'})), ...vehicleTrailers];
                
                if (allTrailers.length === 0) {
                  return <p className="text-slate-500 text-center py-4">No trailers registered</p>;
                }
                
                return (
                  <div className="space-y-2">
                    {allTrailers.map(t => {
                      const alert = serviceAlerts?.trailers.alerts.critical.find(a => a.id === t.id) ||
                                   serviceAlerts?.trailers.alerts.warning.find(a => a.id === t.id) ||
                                   serviceAlerts?.trailers.alerts.attention.find(a => a.id === t.id) ||
                                   serviceAlerts?.trailers.alerts.ok.find(a => a.id === t.id);
                      
                      return (
                        <div key={t.id} className="flex justify-between items-center p-3 bg-slate-50 rounded hover:bg-slate-100">
                          <div>
                            <p className="font-mono font-bold">{t.registration_number}</p>
                            <p className="text-sm text-slate-500">
                              {t.trailer_type || t.vehicle_type} • {t.make}
                              {t.source === 'vehicles' && <span className="text-xs text-blue-500 ml-2">(from fleet)</span>}
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right text-sm">
                              {t.next_service_due && (
                                <p>Due: {new Date(t.next_service_due).toLocaleDateString()}</p>
                              )}
                            </div>
                            {alert && getStatusBadge(alert.status)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fire Extinguishers Tab */}
        <TabsContent value="extinguishers">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }} className="uppercase">
                Fire Extinguishers
              </CardTitle>
              <Button onClick={() => setShowExtinguisherDialog(true)} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Extinguisher
              </Button>
            </CardHeader>
            <CardContent>
              {extinguishers.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No fire extinguishers registered</p>
              ) : (
                <div className="space-y-2">
                  {extinguishers.map(e => {
                    const alert = serviceAlerts?.fire_extinguishers.alerts.critical.find(a => a.id === e.id) ||
                                 serviceAlerts?.fire_extinguishers.alerts.warning.find(a => a.id === e.id) ||
                                 serviceAlerts?.fire_extinguishers.alerts.attention.find(a => a.id === e.id) ||
                                 serviceAlerts?.fire_extinguishers.alerts.ok.find(a => a.id === e.id);
                    
                    return (
                      <div key={e.id} className="flex justify-between items-center p-3 bg-slate-50 rounded hover:bg-slate-100">
                        <div className="flex items-center gap-3">
                          <Flame className="w-5 h-5 text-red-500" />
                          <div>
                            <p className="font-mono font-bold">{e.serial_number}</p>
                            <p className="text-sm text-slate-500">
                              {e.extinguisher_type} {e.size_kg}kg • on {e.entity_registration}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right text-sm">
                            {e.next_service_due && (
                              <p>Due: {new Date(e.next_service_due).toLocaleDateString()}</p>
                            )}
                          </div>
                          {alert && getStatusBadge(alert.status)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Record KM Dialog */}
      <Dialog open={showKMDialog} onOpenChange={setShowKMDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }} className="uppercase">
              Record KM Reading
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Vehicle *</Label>
              <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
                <SelectTrigger data-testid="km-vehicle-select">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.filter(v => v.vehicle_type?.toLowerCase() !== 'trailer').map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.registration_number} - Current: {v.current_km?.toLocaleString() || 0} km
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Current Odometer Reading (km) *</Label>
              <Input
                type="number"
                value={kmReading}
                onChange={(e) => setKmReading(e.target.value)}
                placeholder="Enter current km"
                data-testid="km-reading-input"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={kmNotes}
                onChange={(e) => setKmNotes(e.target.value)}
                placeholder="Optional notes"
                rows={2}
              />
            </div>
            {isAdmin && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
                <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="km-override-label">
                  <input
                    type="checkbox"
                    checked={kmOverride}
                    onChange={(e) => setKmOverride(e.target.checked)}
                    className="mt-1"
                    data-testid="km-override-checkbox"
                  />
                  <span>
                    <span className="font-semibold text-amber-900">Correct mistake (admin override)</span>
                    <span className="block text-xs text-amber-800 mt-1">
                      Allows saving a KM reading that is lower than the vehicle&apos;s current value.
                      The reading will be flagged as a CORRECTION in the audit log.
                    </span>
                  </span>
                </label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowKMDialog(false); setKmOverride(false); }}>Cancel</Button>
            <Button onClick={handleRecordKM} disabled={submitting} data-testid="save-km-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Reading
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Service Dialog */}
      <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }} className="uppercase">
              Record Service Completed
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Service Type *</Label>
              <Select 
                value={serviceForm.service_type} 
                onValueChange={(v) => setServiceForm({
                  ...serviceForm, 
                  service_type: v,
                  entity_type: v === 'vehicle_service' ? 'vehicle' : 
                              v === 'trailer_service' ? 'trailer' : 'fire_extinguisher'
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vehicle_service">Vehicle Service</SelectItem>
                  <SelectItem value="trailer_service">Trailer Service</SelectItem>
                  <SelectItem value="fire_extinguisher">Fire Extinguisher Service</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{serviceForm.entity_type === 'fire_extinguisher' ? 'Fire Extinguisher' : 'Vehicle/Trailer'} *</Label>
              <Select 
                value={serviceForm.entity_id} 
                onValueChange={(v) => {
                  let entity;
                  if (serviceForm.entity_type === 'vehicle') {
                    entity = vehicles.find(x => x.id === v);
                    setServiceForm({
                      ...serviceForm,
                      entity_id: v,
                      entity_registration: entity?.registration_number || ''
                    });
                  } else if (serviceForm.entity_type === 'trailer') {
                    entity = trailers.find(x => x.id === v);
                    setServiceForm({
                      ...serviceForm,
                      entity_id: v,
                      entity_registration: entity?.registration_number || ''
                    });
                  } else {
                    entity = extinguishers.find(x => x.id === v);
                    setServiceForm({
                      ...serviceForm,
                      entity_id: v,
                      entity_registration: entity?.serial_number || ''
                    });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {serviceForm.entity_type === 'vehicle' && vehicles.filter(v => v.vehicle_type?.toLowerCase() !== 'trailer').map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.registration_number}</SelectItem>
                  ))}
                  {serviceForm.entity_type === 'trailer' && trailers.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.registration_number}</SelectItem>
                  ))}
                  {serviceForm.entity_type === 'fire_extinguisher' && extinguishers.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.serial_number} ({e.entity_registration})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Service Date *</Label>
                <Input
                  type="date"
                  value={serviceForm.service_date}
                  onChange={(e) => setServiceForm({...serviceForm, service_date: e.target.value})}
                />
              </div>
              {serviceForm.entity_type === 'vehicle' && (
                <div>
                  <Label>Service KM</Label>
                  <Input
                    type="number"
                    value={serviceForm.service_km}
                    onChange={(e) => setServiceForm({...serviceForm, service_km: e.target.value})}
                    placeholder="Odometer at service"
                  />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Service Provider</Label>
                <Input
                  value={serviceForm.service_provider}
                  onChange={(e) => setServiceForm({...serviceForm, service_provider: e.target.value})}
                  placeholder="Workshop name"
                />
              </div>
              <div>
                <Label>Invoice Number</Label>
                <Input
                  value={serviceForm.invoice_number}
                  onChange={(e) => setServiceForm({...serviceForm, invoice_number: e.target.value})}
                  placeholder="INV-001"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={serviceForm.notes}
                onChange={(e) => setServiceForm({...serviceForm, notes: e.target.value})}
                placeholder="Service details, parts replaced, etc."
                rows={2}
              />
            </div>
            <div>
              <Label>Upload Invoice (optional)</Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
                className="cursor-pointer"
              />
              {invoiceFile && (
                <p className="text-sm text-emerald-600 mt-1">
                  <FileText className="w-4 h-4 inline mr-1" />
                  {invoiceFile.name}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowServiceDialog(false)}>Cancel</Button>
            <Button onClick={handleRecordService} disabled={submitting} data-testid="save-service-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Fire Extinguisher Dialog */}
      <Dialog open={showExtinguisherDialog} onOpenChange={setShowExtinguisherDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }} className="uppercase">
              Add Fire Extinguisher
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Serial Number *</Label>
              <Input
                value={extinguisherForm.serial_number}
                onChange={(e) => setExtinguisherForm({...extinguisherForm, serial_number: e.target.value})}
                placeholder="FE-001"
                data-testid="ext-serial-input"
              />
            </div>
            <div>
              <Label>Attach To *</Label>
              <Select 
                value={extinguisherForm.entity_type} 
                onValueChange={(v) => setExtinguisherForm({
                  ...extinguisherForm, 
                  entity_type: v,
                  entity_id: '',
                  entity_registration: ''
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vehicle">Vehicle</SelectItem>
                  <SelectItem value="trailer">Trailer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{extinguisherForm.entity_type === 'vehicle' ? 'Vehicle' : 'Trailer'} *</Label>
              <Select 
                value={extinguisherForm.entity_id} 
                onValueChange={(v) => {
                  const list = extinguisherForm.entity_type === 'vehicle' ? vehicles : trailers;
                  const entity = list.find(x => x.id === v);
                  setExtinguisherForm({
                    ...extinguisherForm,
                    entity_id: v,
                    entity_registration: entity?.registration_number || ''
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {(extinguisherForm.entity_type === 'vehicle' ? vehicles : trailers).map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.registration_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select 
                  value={extinguisherForm.extinguisher_type} 
                  onValueChange={(v) => setExtinguisherForm({...extinguisherForm, extinguisher_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DCP">DCP (Dry Chemical)</SelectItem>
                    <SelectItem value="CO2">CO2</SelectItem>
                    <SelectItem value="Foam">Foam</SelectItem>
                    <SelectItem value="Water">Water</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Size (kg)</Label>
                <Input
                  type="number"
                  value={extinguisherForm.size_kg}
                  onChange={(e) => setExtinguisherForm({...extinguisherForm, size_kg: parseFloat(e.target.value)})}
                />
              </div>
            </div>
            <div>
              <Label>Last Service Date</Label>
              <Input
                type="date"
                value={extinguisherForm.last_service_date}
                onChange={(e) => setExtinguisherForm({...extinguisherForm, last_service_date: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExtinguisherDialog(false)}>Cancel</Button>
            <Button onClick={handleAddExtinguisher} disabled={submitting} data-testid="save-ext-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Add Extinguisher
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Record Detail Dialog */}
      <Dialog
        open={showServiceDetailDialog}
        onOpenChange={(open) => {
          setShowServiceDetailDialog(open);
          if (!open) {
            setEditingRecord(false);
            setEditForm({});
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }} className="uppercase">
              Service Record Details
            </DialogTitle>
          </DialogHeader>
          {selectedServiceRecord && !editingRecord && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-2xl font-mono font-bold text-slate-900">
                  {selectedServiceRecord.entity_registration}
                </p>
                <Badge className="mt-2">
                  {selectedServiceRecord.service_type?.replace(/_/g, ' ')}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Service Date</p>
                  <p className="font-semibold">
                    {selectedServiceRecord.service_date 
                      ? new Date(selectedServiceRecord.service_date).toLocaleDateString() 
                      : 'N/A'}
                  </p>
                </div>
                {selectedServiceRecord.service_km && (
                  <div>
                    <p className="text-sm text-slate-500">Odometer at Service</p>
                    <p className="font-semibold font-mono">
                      {selectedServiceRecord.service_km?.toLocaleString()} km
                    </p>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Service Provider</p>
                  <p className="font-semibold">
                    {selectedServiceRecord.service_provider || 'Not specified'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Invoice Number</p>
                  <p className="font-semibold font-mono">
                    {selectedServiceRecord.invoice_number || 'N/A'}
                  </p>
                </div>
              </div>
              
              {selectedServiceRecord.next_service_due && (
                <div className="grid grid-cols-2 gap-4 border-t pt-4">
                  <div>
                    <p className="text-sm text-slate-500">Next Service Due</p>
                    <p className="font-semibold">
                      {new Date(selectedServiceRecord.next_service_due).toLocaleDateString()}
                    </p>
                  </div>
                  {selectedServiceRecord.next_service_km && (
                    <div>
                      <p className="text-sm text-slate-500">Next Service KM</p>
                      <p className="font-semibold font-mono">
                        {selectedServiceRecord.next_service_km?.toLocaleString()} km
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {selectedServiceRecord.notes && (
                <div className="border-t pt-4">
                  <p className="text-sm text-slate-500">Notes</p>
                  <p className="text-slate-700 whitespace-pre-wrap">
                    {selectedServiceRecord.notes}
                  </p>
                </div>
              )}
              
              <div className="border-t pt-4">
                <p className="text-sm text-slate-500 mb-2">Invoice Document</p>
                {selectedServiceRecord.invoice_url ? (
                  <a 
                    href={`${process.env.REACT_APP_BACKEND_URL}${selectedServiceRecord.invoice_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <FileText className="w-5 h-5" />
                    View / Download Invoice
                  </a>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-amber-600">No invoice uploaded</p>
                    <Input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file && selectedServiceRecord?.id) {
                          try {
                            await serviceAPI.uploadInvoice(selectedServiceRecord.id, file);
                            toast.success('Invoice uploaded successfully');
                            const updatedRecords = await serviceAPI.listServiceRecords({ limit: 50 });
                            setServiceRecords(updatedRecords.data);
                            const updated = updatedRecords.data.find(r => r.id === selectedServiceRecord.id);
                            if (updated) setSelectedServiceRecord(updated);
                          } catch (error) {
                            console.error('Failed to upload invoice:', error);
                            toast.error('Failed to upload invoice');
                          }
                        }
                      }}
                      className="cursor-pointer max-w-xs"
                      data-testid="upload-invoice-input"
                    />
                    <p className="text-xs text-slate-500">Accepted: PDF, JPG, PNG</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedServiceRecord && editingRecord && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 p-3 rounded text-sm text-amber-800">
                Correcting a mistake? Update the fields below then click Save. The vehicle&apos;s Next Service Due/KM will be recomputed automatically from the most recent remaining record.
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Service Date</Label>
                  <Input
                    type="date"
                    value={(editForm.service_date || '').substring(0, 10)}
                    onChange={(e) => setEditForm({ ...editForm, service_date: e.target.value })}
                    data-testid="edit-service-date"
                  />
                </div>
                <div>
                  <Label>Odometer (km)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editForm.service_km ?? ''}
                    onChange={(e) => setEditForm({ ...editForm, service_km: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                    data-testid="edit-service-km"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Service Provider</Label>
                  <Input
                    value={editForm.service_provider || ''}
                    onChange={(e) => setEditForm({ ...editForm, service_provider: e.target.value })}
                    data-testid="edit-service-provider"
                  />
                </div>
                <div>
                  <Label>Invoice Number</Label>
                  <Input
                    value={editForm.invoice_number || ''}
                    onChange={(e) => setEditForm({ ...editForm, invoice_number: e.target.value })}
                    data-testid="edit-invoice-number"
                  />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  rows={3}
                  value={editForm.notes || ''}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  data-testid="edit-notes"
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            {selectedServiceRecord && !editingRecord && isSupervisor && (
              <>
                <Button
                  variant="outline"
                  className="text-slate-700"
                  onClick={() => {
                    setEditForm({
                      service_date: selectedServiceRecord.service_date || '',
                      service_km: selectedServiceRecord.service_km ?? null,
                      service_provider: selectedServiceRecord.service_provider || '',
                      invoice_number: selectedServiceRecord.invoice_number || '',
                      notes: selectedServiceRecord.notes || '',
                    });
                    setEditingRecord(true);
                  }}
                  data-testid="edit-service-record-btn"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={async () => {
                    if (!window.confirm('Delete this service record? The vehicle\'s next-service info will be recomputed from the previous service.')) return;
                    try {
                      await serviceAPI.deleteServiceRecord(selectedServiceRecord.id);
                      toast.success('Service record deleted');
                      setShowServiceDetailDialog(false);
                      fetchData();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to delete record');
                    }
                  }}
                  data-testid="delete-service-record-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </>
            )}
            {editingRecord && (
              <>
                <Button
                  variant="outline"
                  onClick={() => { setEditingRecord(false); setEditForm({}); }}
                  disabled={savingEdit}
                  data-testid="cancel-edit-service-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      setSavingEdit(true);
                      const payload = { ...editForm };
                      if (payload.service_date && payload.service_date.length === 10) {
                        payload.service_date = new Date(payload.service_date + 'T00:00:00').toISOString();
                      }
                      const res = await serviceAPI.updateServiceRecord(selectedServiceRecord.id, payload);
                      toast.success('Service record updated');
                      setSelectedServiceRecord(res.data?.record || selectedServiceRecord);
                      setEditingRecord(false);
                      fetchData();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to update record');
                    } finally {
                      setSavingEdit(false);
                    }
                  }}
                  disabled={savingEdit}
                  data-testid="save-edit-service-btn"
                >
                  <Save className="w-4 h-4 mr-1" />
                  {savingEdit ? 'Saving...' : 'Save'}
                </Button>
              </>
            )}
            {!editingRecord && (
              <Button variant="outline" onClick={() => setShowServiceDetailDialog(false)}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceSchedulePage;
